import { useState } from 'react';
import { useParams } from 'react-router';
import { toast } from 'react-toastify';
import {
  currencyFormatter,
  ICreatePaymentPromiseRequest,
  PaymentPromiseSource,
  toDecimal,
} from 'shared';

import { Button } from '../../../../common/Atoms/Button';
import { Modal } from '../../../../common/Atoms/Modal';
import { Paragraph } from '../../../../common/Atoms/Typography/Paragraph';
import { DatePopover } from '../../../../common/Components/DatePopover';
import { useGetSelectedOrganisation } from '../../../hooks/useGetSelectedOrganisation';
import {
  useCancelInvoicePromiseMutation,
  useCreateInvoicePromiseMutation,
  useFetchInvoicePromisesQuery,
} from '../../../services/api/invoiceApi/invoice';
import { PaymentPromiseForm } from '../../Common/PaymentPromiseForm';
import { FromCall } from '../../Molecules/CallTranscript';
import { DetailField } from '../InvoiceV2/DetailField';

const sourceLabel: Record<PaymentPromiseSource, string> = {
  [PaymentPromiseSource.USER]: `recorded in app`,
  [PaymentPromiseSource.PORTAL]: `submitted in portal`,
  [PaymentPromiseSource.ESCALATION]: `from an escalation`,
  [PaymentPromiseSource.CALL]: `from a call`,
};

interface PaymentPromiseProps {
  amountDueCents: number;
  currency: string;
  unpaid: boolean;
  /** Short summary for the invoice details card */
  compact?: boolean;
}

export function PaymentPromise({ amountDueCents, currency, unpaid, compact }: PaymentPromiseProps) {
  const { organisationId, invoiceId } = useParams();
  const organisation = useGetSelectedOrganisation();
  const { data: promises } = useFetchInvoicePromisesQuery({ organisationId, invoiceId }, { skip: !organisationId || !invoiceId });
  const [createPromise, { isLoading: creating }] = useCreateInvoicePromiseMutation();
  const [cancelPromise, { isLoading: cancelling }] = useCancelInvoicePromiseMutation();
  const [open, setOpen] = useState(false);

  const current = promises?.find(p => p.status === `open`);
  const format = currencyFormatter(currency);

  async function onSubmit(promise: ICreatePaymentPromiseRequest) {
    try {
      await createPromise({ organisationId, invoiceId, ...promise }).unwrap();
      toast.success(`Payment promise recorded`);
      setOpen(false);
    }
    catch (err) {
      toast.error(err?.data?.message || err?.data || `Could not record the payment promise`);
    }
  }

  async function onCancel() {
    if (!current) return;

    try {
      await cancelPromise({ organisationId, invoiceId, promiseId: current.id }).unwrap();
    }
    catch {
      // Error toast is shown by the API middleware
      return;
    }

    // The accounting system API can't clear an expected payment date, so it stays after cancelling
    if (current.updateExpectedPaymentDate) {
      toast.warn(`Promise cancelled. The expected payment date it set is still on the invoice, change it in ${organisation.accountingSystemName} if needed.`, {
        autoClose: 10000,
      });
    }
  }

  const modal = (
    <Modal
      open={ open }
      setOpen={ setOpen }
      title={ `Record a payment promise` }
    >
      <PaymentPromiseForm
        amountDueCents={ amountDueCents }
        currency={ currency }
        onSubmit={ onSubmit }
        onCancel={ () => setOpen(false) }
        loading={ creating }
      />
    </Modal>
  );

  if (compact) {
    return (
      <DetailField label={ `Payment promise` }>
        { current ? (
          <span>
            { `${format(toDecimal(current.promisedAmountCents))} by ` }
            <DatePopover
              disablePopover
              date={ current.promisedDate }
              labelFormat={ `d LLL` }
              organisationTimezone={ organisation.validatedTimezone }
            />
            { ` · ` }
            <button
              type={ `button` }
              onClick={ onCancel }
              disabled={ cancelling }
              className={ `cursor-pointer text-gray-500 hover:underline disabled:cursor-not-allowed disabled:opacity-50` }
            >
              { `Cancel` }
            </button>
            { current.contactCallId && organisationId ? (
              <FromCall
                organisationId={ organisationId }
                contactId={ current.contactId }
                contactCallId={ current.contactCallId }
              />
            ) : null }
          </span>
        ) : (
          <button
            type={ `button` }
            onClick={ () => setOpen(true) }
            disabled={ !unpaid }
            className={ `cursor-pointer text-paidniceGreen-600 hover:underline disabled:cursor-not-allowed disabled:text-gray-400 disabled:no-underline` }
          >
            { unpaid ? `Record promise` : `Not available` }
          </button>
        ) }
        { modal }
      </DetailField>
    );
  }

  return (
    <div className={ `flex justify-between pt-2` }>
      <Paragraph>
        { `Payment Promise` }
      </Paragraph>
      { current ? (
        <div className={ `flex items-center gap-2` }>
          <Paragraph variant={ `secondary` }>
            { `${format(toDecimal(current.promisedAmountCents))} by ` }
            <DatePopover
              date={ current.promisedDate }
              labelFormat={ `ccc dd LLL yyyy` }
              organisationTimezone={ organisation.validatedTimezone }
            />
            { ` (${sourceLabel[current.source] || current.source})` }
          </Paragraph>
          { current.contactCallId && organisationId ? (
            <FromCall
              organisationId={ organisationId }
              contactId={ current.contactId }
              contactCallId={ current.contactCallId }
            />
          ) : null }
          <Button color={ `none` }
            onClick={ onCancel }
            loading={ cancelling }>
            { `Cancel` }
          </Button>
        </div>
      ) : (
        <Button color={ `none` }
          onClick={ () => setOpen(true) }
          disabled={ !unpaid }>
          { unpaid ? `Record promise` : `Not available` }
        </Button>
      ) }

      { modal }
    </div>
  );
}
